// "Por vendedor": filtros, pills con y sin ventas, la tabla con sus totales y la ficha del vendedor.
// Mockup: surekeep/mockups/backoffice/portal-admin-vl-com-pagadas.html
import { useState } from 'react';
import { Button, CopyableId } from '@/kit';
import { formatMoneyCents, formatShortMXN } from '@/shared/lib/format/money';
import {
    CellLines,
    DataTable,
    UnavailableState,
    type DataTableColumn,
} from '@/shared/patterns/DataTable';
import { MasterDetailEmpty, MasterDetailExplorer } from '@/shared/patterns/MasterDetail';
import { ScreenErrorBoundary } from '@/shared/patterns/ScreenErrorBoundary';
import { StatsBar } from '@/shared/patterns/StatsBar';
import { TableToolbar, ToolbarMetric } from '@/shared/patterns/TableToolbar';
import type { CommissionDirectory } from '../../api/useCommissionDirectory';
import { usePayoutRows } from '../../api/usePayoutRows';
import { DEFAULT_PAYOUT_PERIOD, periodText, type PayoutPeriod } from '../../lib/payoutPeriods';
import {
    applyPill,
    buildSellerRows,
    filterSellerRows,
    nextSellerSort,
    pillCounts,
    sortSellerRows,
    totalsOf,
    type SellerPill,
    type SellerRole,
    type SellerRow,
    type SellerSort,
    type SellerSortKey,
} from '../../lib/sellerRows';
import { FIELD, LABEL } from '../fieldStyles';
import { PayoutPeriodSelect } from './PayoutPeriodSelect';
import { SellerDetail } from './SellerDetail';

interface SellersTabProps {
    period: PayoutPeriod;
    onPeriodChange: (period: PayoutPeriod) => void;
    today: string;
    sellerId: string | null;
    onSelect: (sellerId: string) => void;
    directory: CommissionDirectory;
}

export function SellersTab({
    period,
    onPeriodChange,
    today,
    sellerId,
    onSelect,
    directory,
}: SellersTabProps) {
    const [search, setSearch] = useState('');
    const [storeId, setStoreId] = useState('');
    const [role, setRole] = useState<SellerRole>('all');
    const [pill, setPill] = useState<SellerPill>('all');
    const [sort, setSort] = useState<SellerSort>({ key: 'paid', direction: 'desc' });

    const source = usePayoutRows('SELLER', period, today);
    const filtered = filterSellerRows(buildSellerRows(source.rows, directory), {
        search,
        storeId,
        role,
    });
    const counts = pillCounts(filtered);
    const rows = sortSellerRows(applyPill(filtered, pill), sort);
    const totals = totalsOf(rows);
    const when = periodText(period);
    // Sin vendedor en la URL se abre el primero de la tabla, como el mock, cuando las fuentes ya
    // respondieron, y queda fijado: un refetch que reordena la tabla no cambia la ficha abierta.
    const [autoId, setAutoId] = useState<string | null>(null);
    const firstId = rows.at(0)?.sellerId ?? null;
    if (sellerId === null && autoId === null && source.isReady && firstId !== null)
        setAutoId(firstId);
    const selectedId = sellerId ?? autoId;

    const columns: DataTableColumn<SellerRow, SellerSortKey>[] = [
        {
            key: 'name',
            label: 'Nombre',
            sortKey: 'name',
            cell: (row) => (
                <CellLines
                    primary={row.manager ? `${row.name} ★` : row.name}
                    secondary={<CopyableId inline label="ID" value={row.sellerId} />}
                />
            ),
            cellTitle: (row) => (row.manager ? 'Admin tienda' : undefined),
        },
        {
            key: 'store',
            label: 'Tienda',
            width: '140px',
            sortKey: 'store',
            cell: (row) => row.storeName,
        },
        {
            key: 'sales',
            label: 'Ventas',
            width: '70px',
            numeric: true,
            sortKey: 'sales',
            note: when,
            cell: (row) => row.salesCount,
        },
        {
            key: 'placement',
            label: 'Colocación',
            width: '100px',
            numeric: true,
            sortKey: 'placement',
            note: when,
            cell: (row) => formatShortMXN(row.placementCents / 100),
        },
        {
            key: 'paid',
            label: 'Comisiones',
            width: '120px',
            numeric: true,
            sortKey: 'paid',
            note: when,
            cellClassName: () => 'font-semibold',
            cell: (row) => formatMoneyCents(row.paidCents),
        },
    ];

    const filters = (
        <div className="flex flex-col gap-3 p-3">
            <div className="flex items-center justify-between">
                <span className="text-[11px] font-semibold">Filtros</span>
                <button
                    type="button"
                    className="text-[10.5px] text-primary"
                    onClick={() => {
                        setSearch('');
                        setStoreId('');
                        setRole('all');
                        setPill('all');
                        onPeriodChange(DEFAULT_PAYOUT_PERIOD);
                    }}
                >
                    Limpiar
                </button>
            </div>
            <div>
                <label htmlFor="sellers-search" className={LABEL}>
                    Búsqueda
                </label>
                <input
                    id="sellers-search"
                    className={FIELD}
                    placeholder="Vendedor o ID"
                    value={search}
                    onChange={(event) => {
                        setSearch(event.target.value);
                    }}
                />
            </div>
            <PayoutPeriodSelect period={period} onChange={onPeriodChange} today={today} />
            <div>
                <label htmlFor="sellers-store" className={LABEL}>
                    Tienda
                </label>
                <select
                    id="sellers-store"
                    className={FIELD}
                    value={storeId}
                    onChange={(event) => {
                        setStoreId(event.target.value);
                    }}
                >
                    <option value="">Todas</option>
                    {directory.stores.map((store) => (
                        <option key={store.storeId} value={store.storeId}>
                            {store.name}
                        </option>
                    ))}
                </select>
            </div>
            <div>
                <label htmlFor="sellers-role" className={LABEL}>
                    Rol
                </label>
                <select
                    id="sellers-role"
                    className={FIELD}
                    value={role}
                    onChange={(event) => {
                        setRole(event.target.value as SellerRole);
                    }}
                >
                    <option value="all">Todos</option>
                    <option value="manager">Admin tienda</option>
                    <option value="seller">Vendedor</option>
                </select>
            </div>
            <p className="text-[10px] text-muted-foreground">
                La tienda es la de base del vendedor. Solo aparecen vendedores con ventas o ajustes
                en el periodo.
            </p>
        </div>
    );

    const list = (
        <div className="flex min-h-0 flex-1 flex-col">
            <StatsBar
                items={[
                    { key: 'all', label: 'Todos', value: counts.all },
                    { key: 'with', label: 'Con ventas', value: counts.with, tone: 'success' },
                    { key: 'without', label: 'Sin ventas', value: counts.without, tone: 'neutral' },
                ]}
                active={pill}
                onChange={setPill}
                ariaLabel="Vendedores con y sin ventas"
            />
            <TableToolbar>
                <div className="flex-1" />
                <ToolbarMetric label="Ventas" value={totals.salesCount} />
                <ToolbarMetric label="Colocación" value={formatMoneyCents(totals.placementCents)} />
                <ToolbarMetric
                    label={`Comisiones · ${when}`}
                    value={formatMoneyCents(totals.paidCents)}
                />
            </TableToolbar>
            <div className="min-h-0 flex-1 overflow-auto">
                {source.error ? (
                    <UnavailableState
                        title="No se pudieron cargar las comisiones"
                        body={source.error.message}
                        action={
                            <Button variant="outline" size="sm" onClick={source.refetch}>
                                Reintentar
                            </Button>
                        }
                    />
                ) : (
                    <DataTable
                        columns={columns}
                        rows={rows}
                        rowKey={(row) => row.sellerId}
                        caption="Comisiones por vendedor"
                        sort={sort}
                        onSortToggle={(key) => {
                            setSort(nextSellerSort(sort, key));
                        }}
                        isRowSelected={(row) => row.sellerId === selectedId}
                        onRowClick={(row) => {
                            onSelect(row.sellerId);
                        }}
                        isLoading={source.isLoading}
                        empty={
                            source.rows.length === 0
                                ? 'Sin comisiones en este periodo.'
                                : 'Sin vendedores que cumplan el filtro.'
                        }
                        density="compact"
                    />
                )}
            </div>
        </div>
    );

    const detail = selectedId ? (
        <ScreenErrorBoundary
            title="No se pudo mostrar el detalle del vendedor"
            resetKeys={[selectedId]}
        >
            <SellerDetail key={selectedId} sellerId={selectedId} directory={directory} />
        </ScreenErrorBoundary>
    ) : (
        <MasterDetailEmpty
            title="Selecciona un vendedor"
            sub="Elige una fila para ver sus comisiones por semana y por qué llegó a ese monto."
        />
    );

    return (
        <MasterDetailExplorer
            filters={filters}
            list={list}
            detail={detail}
            storageKey="comisiones-pagadas-vendedor"
        />
    );
}
